import React, { Suspense, useEffect, useState } from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Typography } from '@mui/material';
import { deleteBanner, fetchBannerByType, fetchBannerTypes, updateBannerStatus } from 'services/BannerService';
import { Banner, BannerType } from './BannerType';
import ShireImageCard from 'components/ShireImageCard';
import BannerModal from './BannerModal';
import { publishMessage } from 'components/ShireMessage';

const BannerManagement: React.FC = () => {
  const [activeBannerType, setActiveBannerType] = useState<BannerType | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [bannerTypes, setBannerTypes] = useState<BannerType[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);

  useEffect(() => {
    fetchBannerTypes().then((res) => {
      setBannerTypes(res.records);
      setActiveBannerType(res.records[0]);
    });
  }, []);

  useEffect(() => {
    if (activeBannerType) {
      fetchBannerByType(activeBannerType.id).then((res) => {
        setBanners(res.records);
      });
    }
  }, [activeBannerType]);

  const refreshBanners = async () => {
    const res = await fetchBannerByType(activeBannerType?.id || 0);
    setBanners(res.records);
  }

  const handlePostCreate = () => {
    setOpen(false);
    refreshBanners();
  }

  const handleBannerTypeChange = (e: SelectChangeEvent<number>) => {
    setActiveBannerType(bannerTypes?.find(type => type.id === e.target.value) || null);
  };

  const handleToggleActive = async (bannerId: number, isActive: boolean) => {
    await updateBannerStatus(bannerId, isActive);
    await refreshBanners();
  }

  const handleDeleteBanner = async (bannerId: number) => {
    await deleteBanner(bannerId);
    await refreshBanners();
  }

  return <>
    <Typography variant="h4" sx={{ marginBottom: 3 }}>Quản lý banner</Typography>
    <Paper elevation={3} sx={{ padding: 2 }}>
      <FormControl fullWidth sx={{ marginTop: 5 }}>
        <InputLabel id="banner-type-label">Loại banner</InputLabel>
        <Select
          labelId="banner-type-label"
          id="banner-type-select"
          value={activeBannerType?.id || 0}
          label="Loại banner"
          onChange={handleBannerTypeChange}
        >
          {bannerTypes?.map(type => (
            <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 5 }}>
        <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          Thêm banner
        </Button>
      </Box>
      <Box sx={{ marginY: 3 }}>
        <Grid container spacing={2}>
          {banners?.map((banner) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={banner.id}>
              <ShireImageCard
                id={banner.id}
                title={banner.title}
                description={banner.description}
                imageUrl={banner.imageUrl}
                isActive={banner.isActive}
                onToggleActive={(id, isActive) => handleToggleActive(Number(id), isActive)}
                onDelete={(id) => {
                  handleDeleteBanner(Number(id));
                }}
                onEdit={(id) => {
                  publishMessage({
                    message: 'Chỉnh sửa banner chưa được triển khai',
                    variant: 'warning',
                  });
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
    <Suspense fallback={<div>Loading...</div>}>
      <BannerModal
        open={open}
        onClose={() => setOpen(false)}
        handlePostCreate={handlePostCreate}
        bannerType={activeBannerType || bannerTypes[0] || {
          id: 0,
          name: "",
          description: "",
          minWidth: 0,
          minHeight: 0,
        }}
      />
    </Suspense>

  </>;
};

export default BannerManagement; 